// final

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "./context/AuthContext";
import LoginPage from "./components/LoginPage";
import UnityRoom from "./components/TherapyRooms/UnityRoom";
import MeetingJoinInvitation from "./components/TherapyRooms/MeetingJoinInvitation";
import TestPage from "./components/TherapyRooms/TestPage";
import AdminPage from "./pages/admin/AdminPage";
import TherapistAdminPage from "./pages/therapistAdmin/TherapistAdminPage";
import allowedUserIds from "./pages/admin/adminConfig";
import ClientTherapyRooms from "./components/TherapyRooms/ClientTherapyRooms";
import TherapistTherapyRooms from "./components/TherapyRooms/TherapistTherapyRooms";
import CreateMeetingDashboard from "./pages/admin/CreateMeetingDashboard";
import AdminRegisterUser from "./pages/admin/AdminRegisterUser";
import AdminTherapists from "./pages/admin/AdminTherapists";
import AdminAccountLookup from "./pages/admin/AdminAccountLookup";
import TherapistCreateMeetingDashboard from "./pages/therapistAdmin/TherapistCreateMeetingDashboard";
import TherapistAdminResendPasswordDashboard from "./pages/therapistAdmin/TherapistAdminResendPasswordDashboard";
import TherapistPresignLinkGenerator from "./pages/therapistAdmin/PresignLinkGenerator/TherapistPresignLinkGenerator";
import AdminResendPasswordDashboard from "./pages/admin/AdminResendPasswordDashboard";
import DemoPage from "./components/Demo/DemoPage";
import PresignLinkGenerator from "./pages/admin/PresignLinkGenerator/PresignLinkGenerator";
import RegisterCompanion from "./components/RegisterationForms/RegisterCompanion";
import RegisterTherapy from "./components/RegisterationForms/RegisterTherapy";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AccountDeletionPage from "./pages/AccountDeletionPage";
import Env from "./runtimeEnv";
import { isDemoMode } from "./demo/demoMode";

import axios from "axios";

const PORTAL_IDLE_TIMEOUT_MS = 75 * 60 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 15 * 1000;

function App() {
  const navigate = useNavigate();

  const {
    currentNickname,
    setCurrentNickname,
    PlutonicuserID,
    setPlutonicuserID,
    isAuthenticated,
    setIsAuthenticated,
    isAdmin,
    setIsAdmin,
    userType,
    setUserType,
    loading,
    setLoading,
    currentFormType,
    setCurrentFormType,
    handleLogout,
  } = useContext(AuthContext);

  const [accessToken, setAccessToken] = useState("");
  const idleTimerRef = useRef(null);
  const lastActivityWriteRef = useRef(0);
  const nativeUnityActiveRef = useRef(false);
  const nativeUnityStartedAtRef = useRef(0);

  useEffect(() => {
    const storedUserType = localStorage.getItem("userType");
    if (storedUserType) {
      setUserType(storedUserType);
    }
  }, []);

  const parseLastActiveTime = useCallback((storedValue) => {
    if (!storedValue) return null;

    const numericValue = Number(storedValue);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      return numericValue;
    }

    const legacyDateValue = Date.parse(storedValue);
    return Number.isNaN(legacyDateValue) ? null : legacyDateValue;
  }, []);

  // Synchronize state with localStorage on app load
  useEffect(() => {
    const restoreSession = async () => {
      const savedNickname = localStorage.getItem("currentNickname");
      const savedPlutonicuserID = localStorage.getItem("PlutonicuserID");
      const lastActiveTime = localStorage.getItem("lastActiveTime");
      const sessionActive = sessionStorage.getItem("sessionActive");

      const lastActiveTimestamp = parseLastActiveTime(lastActiveTime);
      if (
        lastActiveTimestamp &&
        Date.now() - lastActiveTimestamp >= PORTAL_IDLE_TIMEOUT_MS
      ) {
        await handleLogout();
        return;
      }

      if (!sessionActive) {
        // console.log('[App] Initializing session...');
        sessionStorage.setItem("sessionActive", "true");
        setIsAuthenticated(false);
      } else if (savedNickname && savedPlutonicuserID) {
        // console.log('[App] Restoring user state from localStorage...');
        setCurrentNickname(savedNickname);
        setPlutonicuserID(savedPlutonicuserID);
        setIsAuthenticated(true);
        const storedUserType = localStorage.getItem("userType");
        setIsAdmin(
          allowedUserIds.includes(savedPlutonicuserID) ||
          (isDemoMode && storedUserType?.toLowerCase() === "admin")
        );
        if (storedUserType) setUserType(storedUserType);

        // Restore the last visited page
        const lastVisitedPage = localStorage.getItem("lastVisitedPage") || "/";
        if (
  lastVisitedPage &&
  window.location.pathname === "/" &&
  !window.history.state?.usr?.tab
) {
  navigate(lastVisitedPage, { replace: true });
}
      }

      setLoading(false); // End loading once the session is restored
    };

    restoreSession();
  }, [handleLogout, navigate, parseLastActiveTime, setCurrentNickname,
      setIsAdmin, setIsAuthenticated, setLoading, setPlutonicuserID,
      setUserType]);

  // Handle tab close to set online status to false
  useEffect(() => {
    const handleTabClose = () => {
      localStorage.setItem("lastActiveTime", Date.now().toString());

      const meetingID = sessionStorage.getItem("meetingID");
      const PlutonicuserID = localStorage.getItem("PlutonicuserID");

      if (meetingID && PlutonicuserID) {
        const payload = { meetingID, online: false };
        // console.log('[App] Sending tab close payload:', payload);

        navigator.sendBeacon(
          `${Env.REACT_APP_PYTHON_API_URL}/api/set-online-status/${PlutonicuserID}`,
          JSON.stringify(payload)
        );
      }
    };

    window.addEventListener("beforeunload", handleTabClose);

    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
      return undefined;
    }

    let loggingOut = false;

    const expireSession = () => {
      if (nativeUnityActiveRef.current) return;
      if (loggingOut) return;
      loggingOut = true;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
      handleLogout();
    };

    const scheduleExpiration = (lastActiveTimestamp) => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

      const remaining =
        PORTAL_IDLE_TIMEOUT_MS - (Date.now() - lastActiveTimestamp);
      if (remaining <= 0) {
        expireSession();
        return;
      }

      idleTimerRef.current = setTimeout(expireSession, remaining);
    };

    const checkExpiration = () => {
      if (nativeUnityActiveRef.current) return true;
      const storedTimestamp = parseLastActiveTime(
        localStorage.getItem("lastActiveTime")
      );

      if (
        storedTimestamp &&
        Date.now() - storedTimestamp >= PORTAL_IDLE_TIMEOUT_MS
      ) {
        expireSession();
        return false;
      }

      scheduleExpiration(storedTimestamp || Date.now());
      return true;
    };

    const handleNativeUnityStart = () => {
      nativeUnityActiveRef.current = true;
      nativeUnityStartedAtRef.current = Date.now();
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    };
    const handleNativeUnityReturn = () => {
      if (!nativeUnityActiveRef.current) return;
      nativeUnityActiveRef.current = false;
      const now = Date.now();
      localStorage.setItem("lastActiveTime", now.toString());
      lastActivityWriteRef.current = now;
      scheduleExpiration(now);
    };

    const recordActivity = () => {
      // Older native shells do not dispatch a return event. Their WebView
      // receives input again once Unity closes, so use that as a fallback.
      if (nativeUnityActiveRef.current &&
          Date.now() - nativeUnityStartedAtRef.current >= 3000) {
        handleNativeUnityReturn();
      }
      if (loggingOut || nativeUnityActiveRef.current || document.visibilityState === "hidden") return;

      const now = Date.now();
      if (
        now - lastActivityWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS
      ) {
        localStorage.setItem("lastActiveTime", now.toString());
        lastActivityWriteRef.current = now;
      }
      scheduleExpiration(now);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (nativeUnityActiveRef.current) handleNativeUnityReturn();
      else checkExpiration();
    };
    const handleWindowResume = () => {
      if (nativeUnityActiveRef.current) handleNativeUnityReturn();
      else checkExpiration();
    };

    const activityEvents = [
      "pointerdown",
      "pointermove",
      "keydown",
      "touchstart",
      "scroll",
    ];
    const passiveOptions = { passive: true };

    const existingTimestamp = parseLastActiveTime(
      localStorage.getItem("lastActiveTime")
    );
    if (!existingTimestamp) {
      const now = Date.now();
      localStorage.setItem("lastActiveTime", now.toString());
      lastActivityWriteRef.current = now;
    }

    if (!checkExpiration()) return undefined;

    activityEvents.forEach((event) =>
      window.addEventListener(event, recordActivity, passiveOptions)
    );
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowResume);
    window.addEventListener("pageshow", handleWindowResume);
    window.addEventListener("plutonic-unity-start", handleNativeUnityStart);
    window.addEventListener("plutonic-unity-return", handleNativeUnityReturn);
    window.addEventListener("plutonic-unity-activity", recordActivity);

    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, recordActivity, passiveOptions)
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowResume);
      window.removeEventListener("pageshow", handleWindowResume);
      window.removeEventListener("plutonic-unity-start", handleNativeUnityStart);
      window.removeEventListener("plutonic-unity-return", handleNativeUnityReturn);
      window.removeEventListener("plutonic-unity-activity", recordActivity);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    };
  }, [handleLogout, isAuthenticated, parseLastActiveTime]);

  // Update nickname function
  const updateNickname = (nickname) => {
    setCurrentNickname(nickname);
    if (nickname) {
      localStorage.setItem("currentNickname", nickname);
      setIsAuthenticated(true);
    } else {
      localStorage.removeItem("currentNickname");
      setIsAuthenticated(false);
    }
  };

  if (loading) {
    // Show a loading spinner or message while session restoration is in progress
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={
            loading ? (
              <p className="text-center">Loading...</p>
            ) : isAuthenticated ? (
              (() => {
                switch (userType?.toLowerCase()) {
                  case "therapist":
                    return <TherapistTherapyRooms />;
                  case "client":
                  case "client-ai-companion":
                  case "client-ai-therapy":
                  default:
                    return <ClientTherapyRooms />;
                }
              })()
            ) : (
              <LoginPage />
            )
          }
        />
        <Route
          path="/clients"
          element={
            isAuthenticated ? (
              userType?.toLowerCase() === "therapist" ? (
                <TherapistTherapyRooms />
              ) : (
                <Navigate to="/" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {['helpcenter', 'learningcenter', 'policies'].map((area) => (
          <Route key={area} path={`/${area}/:sectionId`} element={
            isAuthenticated && userType?.toLowerCase() === "therapist"
              ? <TherapistTherapyRooms />
              : <Navigate to="/" replace />
          } />
        ))}

        

        <Route
          path="/clients/:sessionID/overview"
          element={
            isAuthenticated ? (
              userType?.toLowerCase() === "therapist" ? (
                <TherapistTherapyRooms />
              ) : (
                <Navigate to="/" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/unity-room/:roomType"
          element={
            isAuthenticated ? <UnityRoom /> : <Navigate to="/" replace />
          }
        />

        <Route path="/testpage" element={<TestPage />} />
        <Route path="/demo-beach" element={<DemoPage />} />
        {/* <Route path="/npcgpttherapist888" element={
  <AIBotPage
    setCurrentNickname={setCurrentNickname}
    currentNickname={currentNickname}
    handleLogout={handleLogout}
    setFormType={setCurrentFormType}
    PlutonicuserID={PlutonicuserID}
  />
} />  */}
        <Route
          path="/admin"
          element={
            isAdmin ? (
              <AdminPage
                currentNickname={currentNickname}
                setCurrentNickname={updateNickname}
                handleLogout={handleLogout}
                setFormType={setCurrentFormType}
                PlutonicuserID={PlutonicuserID}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route
          path="/admin/schedule"
          element={isAdmin ? <CreateMeetingDashboard /> : <Navigate to="/" replace />}
        />
        <Route
          path="/admin/register-user"
          element={isAdmin ? <AdminRegisterUser /> : <Navigate to="/" replace />}
        />
        <Route path="/admin/therapists" element={isAdmin ? <AdminTherapists /> : <Navigate to="/" replace />} />
        <Route
          path="/admin/account-lookup"
          element={isAdmin ? <AdminAccountLookup /> : <Navigate to="/" replace />}
        />
        <Route
          path="/admin/account-lookup"
          element={isAdmin ? <AdminAccountLookup /> : <Navigate to="/" replace />}
        />
        <Route
          path="/admin/createmeeting"
          element={<CreateMeetingDashboard />}
        />

        <Route
          path="/admin/updatemeeting"
          element={
            isAdmin ? <CreateMeetingDashboard /> : <Navigate to="/" replace />
          }
        />
        <Route
          path="/admin/resendpassword"
          element={
            isAdmin ? (
              <AdminResendPasswordDashboard />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route
          path="/admin/presignlinkgenerator"
          element={
            isAdmin ? <PresignLinkGenerator /> : <Navigate to="/" replace />
          }
        />


        <Route
        path="/clients/schedule"
        element={
          userType?.toLowerCase() === "therapist" ? (
            <TherapistAdminPage
              currentNickname={currentNickname}
              setCurrentNickname={updateNickname}
              handleLogout={handleLogout}
              setFormType={setCurrentFormType}
              PlutonicuserID={PlutonicuserID}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
        />

        <Route
  path="/clients/schedule/session"
  element={isAuthenticated && userType?.toLowerCase() === "therapist" ? <TherapistCreateMeetingDashboard /> : <Navigate to="/" replace />}
  />
  <Route
  path="/clients/schedule/create"
  element={
    userType?.toLowerCase() === "therapist" ? (
      <TherapistCreateMeetingDashboard />
    ) : (
      <Navigate to="/" replace />
    )
  }
/>

        <Route
          path="/clients/schedule/update"
          element={
            userType?.toLowerCase() === "therapist"
              ? <TherapistCreateMeetingDashboard />
              : <Navigate to="/" replace />
          }
        />

        {/* <Route
          path="/therapistAdmin/resendpassword"
          element={
            isAdmin ? (
              <TherapistAdminResendPasswordDashboard />
            ) : (
              <Navigate to="/" replace />
            )
          }
        /> */}

        <Route
          path="/clients/schedule/link-generator"
          element={
            userType?.toLowerCase() === "therapist"
              ? <TherapistPresignLinkGenerator />
              : <Navigate to="/" replace />
          }
        />

        <Route path="/register/ai-companion" element={<RegisterCompanion />} />
        <Route
          path="/register/ai-enabled-therapy"
          element={<RegisterTherapy />}
        />

        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/account-deletion" element={<AccountDeletionPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <MeetingJoinInvitation userID={PlutonicuserID} enabled={isAuthenticated && !loading} />
    </div>
  );
}

export default App;
